import LiveKitConnectionLost from './LiveKitConnectionLost';
import LiveKitVideoRoomContainer from './LiveKitVideoRoomContainer';

import TelemedicineContext from '../../../context/telemedicine';
import useFinishCall, { FINISHED_STATUS } from '../../../hooks/useFinishCall';
import { get as getTelemedicine } from '../../../services/telemedicine';

import { showErrorToast } from '@amigoapp/doca-react';
import { DisconnectReason } from 'livekit-client';
import { useCallback, useContext, useEffect, useState } from 'react';

/**
 * Keeps the patient in the call across a dropped connection.
 *
 * Only the doctor ending the call finishes it. Any other drop (network, the same link opened on
 * another device) shows a reconnect screen instead of the finished one, and reconnecting mounts
 * a fresh room - the dropped one has already released its tracks. The room-closed listener lives
 * here so the doctor ending the call still reaches a patient sitting on the reconnect screen.
 *
 * Reconnecting asks the API first: a patient whose network was down when the doctor ended the
 * call never got room-closed, and joining with the old token would make LiveKit auto-create an
 * empty room. The fresh response also brings a new token, so an expired one never blocks it.
 */
const LiveKitCallSession = () => {
	const { updateTelemedicine } = useContext(TelemedicineContext);
	const finishCall = useFinishCall();

	const [attempt, setAttempt] = useState(0);
	const [isReconnecting, setIsReconnecting] = useState(false);
	const [lostReason, setLostReason] = useState(null);

	const onConnectionLost = useCallback(reason => {
		setLostReason(reason ?? DisconnectReason.UNKNOWN_REASON);
	}, []);

	const onReconnect = useCallback(async () => {
		setIsReconnecting(true);

		const { data } = await getTelemedicine();

		setIsReconnecting(false);

		if (data?.status === FINISHED_STATUS) {
			finishCall();

			return;
		}

		if (!data?.livekit) {
			showErrorToast('Não foi possível reconectar. Verifique sua internet e tente novamente.');

			return;
		}

		updateTelemedicine({ livekit: data.livekit });
		setLostReason(null);
		setAttempt(current => current + 1);
	}, [finishCall, updateTelemedicine]);

	// Leaving the page disconnects the room (see useDisconnectOnUnload). A browser that restores
	// it from the back-forward cache brings back that dead room, so the page starts over instead.
	useEffect(() => {
		const handlePageShow = event => {
			if (event.persisted) {
				window.location.reload();
			}
		};

		window.addEventListener('pageshow', handlePageShow);

		return () => {
			window.removeEventListener('pageshow', handlePageShow);
		};
	}, []);

	useEffect(() => {
		window.socket.on('telemedicine:room-closed', finishCall);

		return () => {
			window.socket.off('telemedicine:room-closed', finishCall);
		};
	}, [finishCall]);

	if (lostReason !== null) {
		return (
			<LiveKitConnectionLost
				isDuplicate={lostReason === DisconnectReason.DUPLICATE_IDENTITY}
				isReconnecting={isReconnecting}
				onReconnect={onReconnect}
			/>
		);
	}

	return <LiveKitVideoRoomContainer key={attempt} onConnectionLost={onConnectionLost} />;
};

export default LiveKitCallSession;
